import React, { useState, useEffect } from 'react';
import api from '../api';

interface AuditLog {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id?: number;
  details: any;
  ip_address: string;
  user_agent: string;
  timestamp: string;
}

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalLogs, setTotalLogs] = useState(0);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState({
    user: '',
    action: '',
    resource_type: '',
    date_from: (() => {
      const d = new Date(); d.setDate(d.getDate() - 7);
      return d.toISOString().split('T')[0];
    })(),
    date_to: ''
  });

  useEffect(() => {
    fetchAuditLogs();
  }, [currentPage, pageSize, filters]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params: any = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize
      };

      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const response = await api.get('/audit-logs/', { params });
      setLogs(response.data.logs || []);
      setTotalLogs(response.data.total || 0);
    } catch (error: any) {
      console.error('Error fetching audit logs:', error);
      // If endpoint doesn't exist, show placeholder
      if (error.response?.status === 404) {
        setLogs([{
          id: 1,
          user_id: 1,
          user_name: 'System',
          user_email: 'system@example.com',
          action: 'info',
          resource_type: 'system',
          details: 'Audit logging system is being set up. Backend endpoints will be available soon.',
          ip_address: '127.0.0.1',
          user_agent: 'System',
          timestamp: new Date().toISOString()
        }]);
        setTotalLogs(1);
      } else {
        setLogs([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      user: '',
      action: '',
      resource_type: '',
      date_from: '',
      date_to: ''
    });
    setCurrentPage(1);
  };

  const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create': return '➕';
      case 'update': return '✏️';
      case 'delete': return '🗑️';
      case 'login': return '🔐';
      case 'logout': return '🚪';
      case 'import': return '📥';
      case 'export': return '📤';
      case 'view': return '👁️';
      default: return '📝';
    }
  };

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create': return '#16a34a';
      case 'update': return '#3b82f6';
      case 'delete': return '#dc2626';
      case 'login': return '#059669';
      case 'logout': return '#6b7280';
      case 'import': return '#7c3aed';
      case 'export': return '#ea580c';
      case 'view': return '#0891b2';
      default: return '#6b7280';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata',
    });
  };

  const formatDetails = (details: any) => {
    if (!details) return '-';
    if (typeof details === 'string') return details;
    return JSON.stringify(details, null, 2);
  };

  const totalPages = Math.ceil(totalLogs / pageSize);
  const startRecord = totalLogs > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endRecord = Math.min(currentPage * pageSize, totalLogs);

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedLogs = [...logs].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = (a as any)[sortField];
    let bVal: any = (b as any)[sortField];
    if (sortField === 'details') {
      aVal = aVal ? (typeof aVal === 'string' ? aVal : JSON.stringify(aVal)) : '';
      bVal = bVal ? (typeof bVal === 'string' ? bVal : JSON.stringify(bVal)) : '';
    }
    if (sortField === 'resource_id') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="card-header" style={{ padding: '12px 16px' }}>
        <h2 className="card-title" style={{ fontSize: '16px', fontWeight: '600' }}>Audit Logs</h2>
        <div style={{ fontSize: '12px', color: '#6c757d' }}>
          {totalLogs} total entries
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        padding: '12px 16px', 
        background: '#f8f9fa', 
        borderBottom: '1px solid #dee2e6',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '0 0 150px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              User
            </label>
            <input
              type="text"
              placeholder="Search user..."
              value={filters.user}
              onChange={(e) => handleFilterChange('user', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            />
          </div>
          
          <div style={{ flex: '0 0 120px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Action
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="import">Import</option>
              <option value="export">Export</option>
              <option value="view">View</option>
            </select>
          </div>
          
          <div style={{ flex: '0 0 120px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Resource
            </label>
            <select
              value={filters.resource_type}
              onChange={(e) => handleFilterChange('resource_type', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              <option value="product">Product</option>
              <option value="category">Category</option>
              <option value="inventory">Inventory</option>
              <option value="order">Order</option>
              <option value="user">User</option>
            </select>
          </div>
          
          <div style={{ flex: '0 0 130px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              From Date
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            />
          </div>
          
          <div style={{ flex: '0 0 130px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              To Date
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            />
          </div>

          {Object.values(filters).some(v => v) && (
            <button
              onClick={clearFilters}
              style={{ 
                padding: '6px 12px',
                fontSize: '12px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-container" style={{ flex: 1, overflow: 'auto' }}>
        <table className="table" style={{ minWidth: '1200px' }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('timestamp')} style={{ width: '140px', cursor: 'pointer' }}>Timestamp {sortField === 'timestamp' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('action')} style={{ width: '80px', cursor: 'pointer' }}>Action {sortField === 'action' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('user_name')} style={{ width: '150px', cursor: 'pointer' }}>User {sortField === 'user_name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('resource_type')} style={{ width: '100px', cursor: 'pointer' }}>Resource {sortField === 'resource_type' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('resource_id')} style={{ width: '80px', cursor: 'pointer' }}>ID {sortField === 'resource_id' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('details')} style={{ width: '200px', cursor: 'pointer' }}>Details {sortField === 'details' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('ip_address')} style={{ width: '120px', cursor: 'pointer' }}>IP Address {sortField === 'ip_address' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('user_agent')} style={{ cursor: 'pointer' }}>User Agent {sortField === 'user_agent' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                  Loading audit logs...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                  No audit logs found
                </td>
              </tr>
            ) : (
              sortedLogs.map((log) => (
                <tr key={log.id}>
                  <td style={{ fontSize: '12px', color: '#495057' }}>
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: getActionColor(log.action),
                      backgroundColor: `${getActionColor(log.action)}20`,
                      textTransform: 'uppercase'
                    }}>
                      {getActionIcon(log.action)} {log.action}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontSize: '13px', fontWeight: '500' }}>
                      {log.user_name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6c757d' }}>
                      {log.user_email}
                    </div>
                  </td>
                  <td style={{ fontSize: '13px', textTransform: 'capitalize' }}>
                    {log.resource_type}
                  </td>
                  <td style={{ fontSize: '13px', color: '#6c757d' }}>
                    {log.resource_id || '-'}
                  </td>
                  <td>
                    <div style={{ 
                      fontSize: '12px', 
                      maxWidth: '200px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {formatDetails(log.details)}
                    </div>
                  </td>
                  <td style={{ fontSize: '12px', color: '#6c757d' }}>
                    {log.ip_address}
                  </td>
                  <td>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#6c757d',
                      maxWidth: '200px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {log.user_agent}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalLogs > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {startRecord} to {endRecord} of {totalLogs} entries
          </div>
          
          <div className="pagination-controls">
            <span>Show:</span>
            <select 
              className="page-size-selector"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            
            <button 
              className="btn btn-secondary"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            
            <span>Page {currentPage} of {totalPages}</span>
            
            <button 
              className="btn btn-secondary"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;